import { gql, useQuery } from '@apollo/client'
import { useSearchHistory } from './useSearchHistory'
import { useDebouncedValue } from './useDebouncedValue'
import { type SearchAutocompleteViewQuery } from '@obeta/schema'

const DEBOUNCE = 90
const MIN_CHARS = 3
const MAX_CHARS = 50

const SEARCH_AUTOCOMPLETE_VIEW_QUERY = gql`
  query SearchAutocompleteView($query: String!) {
    searchAutocompleteView(query: $query) {
      suggestions {
        title
      }
      categories {
        id
        title
      }
      supplier {
        id
        title
        filterName
        image
      }
      stores {
        id
        title
      }
    }
  }
`

export enum ESuggestionGroup {
  Suggestion = 'Suchvorschläge',
  Category = 'Lagersortimentskatalog',
  Supplier = 'Lieferanten',
  Store = 'Filialen',
}

export enum ESuggestionType {
  Suggestion = 'suggestion',
  History = 'history',
  Category = 'category',
  Supplier = 'supplier',
  Store = 'store',
}

export type SuggestionItem = {
  title: string
  group: ESuggestionGroup
  type: ESuggestionType
  image?: string
  id?: string
}

export const useSearchAutocompleteView = (query: string): SuggestionItem[] => {
  const debouncedQuery = useDebouncedValue(
    query.trim().substring(0, MAX_CHARS).toLowerCase(),
    DEBOUNCE
  )

  const { history } = useSearchHistory()

  const autocompleteQuery = useQuery<SearchAutocompleteViewQuery>(SEARCH_AUTOCOMPLETE_VIEW_QUERY, {
    skip: debouncedQuery.length < MIN_CHARS,
    variables: { query: debouncedQuery },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const autocompleteData =
    autocompleteQuery.loading && autocompleteQuery.previousData
      ? autocompleteQuery.previousData?.searchAutocompleteView
      : autocompleteQuery.data?.searchAutocompleteView

  const { suggestions = [], categories = [], supplier = [], stores = [] } = autocompleteData ?? {}

  const groupsWithOptions = [suggestions, categories, supplier, stores].filter(
    (option) => option.length > 0
  ).length

  const maxSuggestions = groupsWithOptions >= 3 ? 2 : groupsWithOptions === 2 ? 3 : 5
  const maxOptions = groupsWithOptions >= 3 ? 3 : groupsWithOptions === 2 ? 5 : 10
  const maxHistory = groupsWithOptions >= 3 ? 1 : groupsWithOptions === 2 ? 2 : 5

  return [
    ...history
      .map((item) => ({
        title: item,
        type: ESuggestionType.History,
        group: ESuggestionGroup.Suggestion,
      }))
      .filter((item) => item.title.toLowerCase().includes(query))
      .reverse()
      .slice(0, maxHistory),

    ...suggestions
      .map((item) => ({
        title: item.title,
        type: ESuggestionType.Suggestion,
        group: ESuggestionGroup.Suggestion,
      }))
      .slice(0, maxSuggestions),

    ...categories
      .map((item) => ({
        id: item.id,
        title: item.title,
        type: ESuggestionType.Category,
        group: ESuggestionGroup.Category,
      }))
      .slice(0, maxOptions),

    ...supplier
      .map((item) => ({
        id: item.id,
        title: item.title,
        filterName: item.filterName,
        image: item.image,
        type: ESuggestionType.Supplier,
        group: ESuggestionGroup.Supplier,
      }))
      .slice(0, maxOptions),

    ...stores
      .map((item) => ({
        id: item.id,
        title: item.title,
        type: ESuggestionType.Store,
        group: ESuggestionGroup.Store,
      }))
      .slice(0, maxOptions),
  ]
}
